/**
 * BeamEffect - Continuous beam visual while channeling
 * Renders a beam from player to target direction with pulsing effects
 */

import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { ElementType, Vector2 } from '@shared/types';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { ELEMENT_INFO } from '@shared/skills/SkillDeliveryTypes';

export interface BeamEffectConfig {
  skill: CustomSkillV2;
  origin: Vector2;
  direction: Vector2;
}

/** Cached offscreen canvas for radial glow (origin flash / impact glow) */
interface BeamRadialCache {
  canvas: HTMLCanvasElement;
  zoom: number;
}

/** Cached offscreen canvas for particle glow */
interface BeamParticleCache {
  canvas: HTMLCanvasElement;
  zoom: number;
}

export class BeamEffect {
  private skill: CustomSkillV2;
  public readonly origin: Vector2;
  private direction: Vector2;
  private time: number = 0;
  private width: number;
  private range: number;
  private element: ElementType;
  private color: string;
  private secondaryColor: string;
  public isComplete: boolean = false;
  private beamMode: 'steady' | 'rampup' | 'overcharge';
  private channelTime: number = 0;
  private actualLength: number = 0;

  // Cached offscreen canvases (per-element, shared across instances)
  private static originCache = new Map<string, BeamRadialCache>();
  private static impactCache = new Map<string, BeamRadialCache>();
  private static particleCache = new Map<string, BeamParticleCache>();

  // Cached main beam gradient (invalidated when endpoints change)
  private cachedMainGrad: CanvasGradient | null = null;
  private cachedMainGradKey: string = '';

  constructor(config: BeamEffectConfig) {
    this.skill = config.skill;
    this.origin = { x: config.origin.x, y: config.origin.y };
    this.direction = this.normalizeVector(config.direction);
    this.element = config.skill.element;
    this.width = SkillCreatorV2.getBeamWidth(config.skill);
    this.range = SkillCreatorV2.getEffectiveRange(config.skill);

    // Read beam mode choice
    this.beamMode = config.skill.beamMode || 'steady';

    // Get colors from element
    const elementInfo = ELEMENT_INFO[this.element];
    this.color = elementInfo.color;
    this.secondaryColor = this.getSecondaryColor(this.element);
  }

  private normalizeVector(v: Vector2): Vector2 {
    const len = Math.sqrt(v.x * v.x + v.y * v.y);
    if (len === 0) return { x: 1, y: 0 };
    return { x: v.x / len, y: v.y / len };
  }

  private getSecondaryColor(element: ElementType): string {
    const colorMap: Record<ElementType, string> = {
      physical: '#c0c0c0',
      od: '#ff6600',
      gok: '#00bfff',
      su: '#80ffff',
      karanlik: '#d19bff',
    };
    return colorMap[element];
  }

  /**
   * Update beam origin and direction (called every frame while channeling)
   * In-place update, no allocation
   */
  updatePosition(origin: Vector2, direction: Vector2): void {
    this.origin.x = origin.x;
    this.origin.y = origin.y;
    const len = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    if (len > 0) {
      this.direction.x = direction.x / len;
      this.direction.y = direction.y / len;
    }
  }

  /**
   * Update only the origin (follows player movement)
   * In-place update, no allocation
   */
  updateOrigin(origin: Vector2): void {
    this.origin.x = origin.x;
    this.origin.y = origin.y;
  }

  /**
   * Stop the beam effect
   */
  stop(): void {
    this.isComplete = true;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;
    this.channelTime += deltaTime;

    // Beam continues until explicitly stopped
    if (!this.skill.isActive) {
      this.isComplete = true;
    }
  }

  /** Get or create cached origin (muzzle flash) offscreen canvas */
  private getOriginCanvas(zoom: number): HTMLCanvasElement {
    const key = this.color;
    const cached = BeamEffect.originCache.get(key);
    if (cached && cached.zoom === zoom) {
      return cached.canvas;
    }

    // Use a reference size; actual size will be scaled at draw time
    const refRadius = 48;
    const size = refRadius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, refRadius);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.3, this.secondaryColor);
    grad.addColorStop(0.6, this.color + '80');
    grad.addColorStop(1, 'transparent');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, refRadius, 0, Math.PI * 2);
    ctx.fill();

    BeamEffect.originCache.set(key, { canvas, zoom });
    return canvas;
  }

  /** Get or create cached impact glow offscreen canvas */
  private getImpactCanvas(zoom: number): HTMLCanvasElement {
    const key = this.color;
    const cached = BeamEffect.impactCache.get(key);
    if (cached && cached.zoom === zoom) {
      return cached.canvas;
    }

    const refRadius = 48;
    const size = refRadius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, refRadius);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.2, this.color);
    grad.addColorStop(0.5, this.secondaryColor + '90');
    grad.addColorStop(0.8, this.color + '40');
    grad.addColorStop(1, 'transparent');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, refRadius, 0, Math.PI * 2);
    ctx.fill();

    BeamEffect.impactCache.set(key, { canvas, zoom });
    return canvas;
  }

  /** Get or create cached particle glow offscreen canvas */
  private getParticleCanvas(zoom: number): HTMLCanvasElement {
    const key = this.color;
    const cached = BeamEffect.particleCache.get(key);
    if (cached && cached.zoom === zoom) {
      return cached.canvas;
    }

    const refRadius = 16;
    const size = refRadius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, refRadius);
    grad.addColorStop(0, 'rgba(255,255,255,0.85)');
    grad.addColorStop(0.5, this.secondaryColor + '66');
    grad.addColorStop(1, 'transparent');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, refRadius, 0, Math.PI * 2);
    ctx.fill();

    BeamEffect.particleCache.set(key, { canvas, zoom });
    return canvas;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (this.isComplete) return;

    const ctx = renderer.context;
    // Offset beam origin to character's chest/hand height (not feet)
    const originScreenRaw = camera.worldToScreen(this.origin.x, this.origin.y);
    const screenOrigin = { x: originScreenRaw.x, y: originScreenRaw.y - 30 * camera.zoom };
    const beamLen = this.actualLength > 0 ? Math.min(this.range, this.actualLength) : this.range;
    const endX = this.origin.x + this.direction.x * beamLen;
    const endY = this.origin.y + this.direction.y * beamLen;
    const screenEnd = camera.worldToScreen(endX, endY);
    const z = camera.zoom;

    const bdx = screenEnd.x - screenOrigin.x;
    const bdy = screenEnd.y - screenOrigin.y;
    const beamLength = Math.sqrt(bdx * bdx + bdy * bdy);

    ctx.save();

    // Overcharge mode: show charging orb at origin instead of beam
    if (this.beamMode === 'overcharge') {
      this.renderOverchargeOrb(ctx, screenOrigin, z);
      ctx.restore();
      return;
    }

    // Animation values
    const t = this.time;
    const fastPulse = Math.sin(t * 20) * 0.15 + 1;
    const slowPulse = Math.sin(t * 6) * 0.1 + 1;
    const flickerIntensity = 0.85 + Math.random() * 0.15;
    const chargeWidthMult = this.beamMode === 'rampup' ? 0.7 + Math.min(0.6, this.channelTime * 0.2) : 1.0;
    const baseWidth = this.width * z * 0.6 * chargeWidthMult;

    // Draw directly from origin to end (no rotation transform needed)
    const dx = screenEnd.x - screenOrigin.x;
    const dy = screenEnd.y - screenOrigin.y;
    const angle = Math.atan2(dy, dx);

    // Perpendicular vector for width
    const perpX = -Math.sin(angle);
    const perpY = Math.cos(angle);

    // === LAYER 1: Wide outer glow (solid color — gradient barely noticeable) ===
    ctx.globalAlpha = 0.3 * flickerIntensity;
    const outerWidth = baseWidth * 3 * slowPulse;

    ctx.beginPath();
    ctx.moveTo(screenOrigin.x + perpX * outerWidth, screenOrigin.y + perpY * outerWidth);
    ctx.lineTo(screenEnd.x + perpX * outerWidth * 0.5, screenEnd.y + perpY * outerWidth * 0.5);
    ctx.lineTo(screenEnd.x - perpX * outerWidth * 0.5, screenEnd.y - perpY * outerWidth * 0.5);
    ctx.lineTo(screenOrigin.x - perpX * outerWidth, screenOrigin.y - perpY * outerWidth);
    ctx.closePath();
    ctx.fillStyle = this.color + '40';
    ctx.fill();

    // === LAYER 2: Electric tendrils ===
    ctx.globalAlpha = 0.6 * flickerIntensity;
    ctx.strokeStyle = this.secondaryColor;
    ctx.lineWidth = 1.5 * z;
    ctx.lineCap = 'round';

    const tendrilCount = 3;
    for (let i = 0; i < tendrilCount; i++) {
      ctx.beginPath();
      const offsetPhase = t * 8 + i * Math.PI * 0.667;
      const tendrilOffset = Math.sin(offsetPhase) * baseWidth * 0.8;

      ctx.moveTo(
        screenOrigin.x + perpX * tendrilOffset * 0.3,
        screenOrigin.y + perpY * tendrilOffset * 0.3
      );

      // Draw jagged lightning path (reduced segments for performance)
      const segments = 6;
      for (let j = 1; j <= segments; j++) {
        const progress = j / segments;
        const jitter = (j < segments) ? (Math.sin(t * 25 + i * 3 + j) * baseWidth * 0.4) : 0;
        const waveOffset = Math.sin(progress * Math.PI * 3 + offsetPhase) * baseWidth * 0.5;

        const px = screenOrigin.x + dx * progress + perpX * (waveOffset + jitter) * (1 - progress * 0.5);
        const py = screenOrigin.y + dy * progress + perpY * (waveOffset + jitter) * (1 - progress * 0.5);
        ctx.lineTo(px, py);
      }
      ctx.stroke();
    }

    // === LAYER 3: Main beam body ===
    ctx.globalAlpha = 0.9 * flickerIntensity;
    const mainWidth = baseWidth * fastPulse;

    // Create tapered beam shape
    ctx.beginPath();
    ctx.moveTo(screenOrigin.x + perpX * mainWidth, screenOrigin.y + perpY * mainWidth);

    // Wavy top edge (reduced segments for performance)
    const waveSegments = 10;
    for (let i = 1; i <= waveSegments; i++) {
      const progress = i / waveSegments;
      const wave = Math.sin(progress * Math.PI * 6 + t * 15) * baseWidth * 0.15;
      const taper = 1 - progress * 0.6;
      const px = screenOrigin.x + dx * progress + perpX * (mainWidth * taper + wave);
      const py = screenOrigin.y + dy * progress + perpY * (mainWidth * taper + wave);
      ctx.lineTo(px, py);
    }

    // Wavy bottom edge (reverse)
    for (let i = waveSegments; i >= 0; i--) {
      const progress = i / waveSegments;
      const wave = Math.sin(progress * Math.PI * 6 + t * 15 + Math.PI) * baseWidth * 0.15;
      const taper = 1 - progress * 0.6;
      const px = screenOrigin.x + dx * progress - perpX * (mainWidth * taper + wave);
      const py = screenOrigin.y + dy * progress - perpY * (mainWidth * taper + wave);
      ctx.lineTo(px, py);
    }
    ctx.closePath();

    // Gradient fill for main beam (cached — only recreated when endpoints change)
    const gradKey = `${Math.round(screenOrigin.x)},${Math.round(screenOrigin.y)},${Math.round(screenEnd.x)},${Math.round(screenEnd.y)}`;
    if (!this.cachedMainGrad || this.cachedMainGradKey !== gradKey) {
      const mainGrad = ctx.createLinearGradient(screenOrigin.x, screenOrigin.y, screenEnd.x, screenEnd.y);
      mainGrad.addColorStop(0, this.color);
      mainGrad.addColorStop(0.3, this.secondaryColor);
      mainGrad.addColorStop(0.7, this.color);
      mainGrad.addColorStop(1, this.secondaryColor + '80');
      this.cachedMainGrad = mainGrad;
      this.cachedMainGradKey = gradKey;
    }
    ctx.fillStyle = this.cachedMainGrad;
    ctx.fill();

    // === LAYER 4: Bright core (solid color — gradient barely noticeable on thin core) ===
    ctx.globalAlpha = 0.95;
    const coreWidth = baseWidth * 0.35 * fastPulse;

    ctx.beginPath();
    ctx.moveTo(screenOrigin.x + perpX * coreWidth, screenOrigin.y + perpY * coreWidth);
    ctx.lineTo(screenEnd.x + perpX * coreWidth * 0.2, screenEnd.y + perpY * coreWidth * 0.2);
    ctx.lineTo(screenEnd.x - perpX * coreWidth * 0.2, screenEnd.y - perpY * coreWidth * 0.2);
    ctx.lineTo(screenOrigin.x - perpX * coreWidth, screenOrigin.y - perpY * coreWidth);
    ctx.closePath();

    ctx.fillStyle = '#ffffffdd';
    ctx.fill();

    // === LAYER 5: Energy particles flowing along beam (cached particle glow) ===
    ctx.globalAlpha = 1;
    const particleCanvas = this.getParticleCanvas(z);
    const pRefRadius = 16;
    const particleCount = Math.floor(beamLength / 25);
    for (let i = 0; i < particleCount; i++) {
      const baseProgress = (i / particleCount + t * 1.5) % 1;
      const wobble = Math.sin(t * 12 + i * 2) * baseWidth * 0.4;

      const px = screenOrigin.x + dx * baseProgress + perpX * wobble;
      const py = screenOrigin.y + dy * baseProgress + perpY * wobble;

      const particleSize = (2 + Math.sin(t * 20 + i) * 1.5) * z;

      // Stamp cached particle glow, scaled to particleSize
      const scale = (particleSize * 2) / pRefRadius;
      const drawSize = particleCanvas.width * scale;
      ctx.drawImage(particleCanvas, px - drawSize / 2, py - drawSize / 2, drawSize, drawSize);
    }

    // === LAYER 6: Origin point (muzzle flash, cached) ===
    ctx.globalAlpha = 0.9;
    const originSize = baseWidth * 1.5 * fastPulse;
    const originCanvas = this.getOriginCanvas(z);
    const oRefRadius = 48;
    const oScale = originSize / oRefRadius;
    const oDrawSize = originCanvas.width * oScale;
    ctx.drawImage(originCanvas, screenOrigin.x - oDrawSize / 2, screenOrigin.y - oDrawSize / 2, oDrawSize, oDrawSize);

    // === LAYER 7: Impact point effect (cached) ===
    ctx.globalAlpha = 0.85;
    const impactPulse = Math.sin(t * 18) * 0.2 + 1;
    const impactSize = baseWidth * 2 * impactPulse;
    const impactCanvas = this.getImpactCanvas(z);
    const iScale = impactSize / oRefRadius;
    const iDrawSize = impactCanvas.width * iScale;
    ctx.drawImage(impactCanvas, screenEnd.x - iDrawSize / 2, screenEnd.y - iDrawSize / 2, iDrawSize, iDrawSize);

    // Impact sparks
    ctx.globalAlpha = 0.8;
    const sparkCount = 6;
    for (let i = 0; i < sparkCount; i++) {
      const sparkAngle = (i / sparkCount) * Math.PI * 2 + t * 5;
      const sparkDist = baseWidth * (1 + Math.sin(t * 12 + i * 2) * 0.5);
      const sparkX = screenEnd.x + Math.cos(sparkAngle) * sparkDist;
      const sparkY = screenEnd.y + Math.sin(sparkAngle) * sparkDist;
      const sparkSize = 3 * z * (0.5 + Math.sin(t * 20 + i) * 0.5);

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(sparkX, sparkY, sparkSize, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /**
   * Get the endpoint of the beam for collision detection
   */
  getEndpoint(): Vector2 {
    // Use actual beam length (capped at range, but can be shorter based on mouse distance)
    const len = Math.min(this.range, this.actualLength || this.range);
    return {
      x: this.origin.x + this.direction.x * len,
      y: this.origin.y + this.direction.y * len,
    };
  }

  /** Set beam length based on mouse distance (clamped to range) */
  setTargetDistance(dist: number): void {
    this.actualLength = Math.min(this.range, Math.max(20, dist));
  }

  /**
   * Get points along the beam for collision detection
   */
  getBeamPoints(segments: number = 10): Vector2[] {
    const points: Vector2[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      points.push({
        x: this.origin.x + this.direction.x * this.range * t,
        y: this.origin.y + this.direction.y * this.range * t,
      });
    }
    return points;
  }

  /**
   * Check if a point is within the beam
   */
  isPointInBeam(point: Vector2): boolean {
    return this.isEntityInBeam(point.x, point.y, 0);
  }

  /**
   * Check if an entity (with hitbox radius) is within the beam
   * More accurate collision that accounts for entity size
   */
  isEntityInBeam(entityX: number, entityY: number, entityRadius: number = 0): boolean {
    const beamLen = this.actualLength > 0 ? Math.min(this.range, this.actualLength) : this.range;
    // Project entity center onto beam line
    const dx = entityX - this.origin.x;
    const dy = entityY - this.origin.y;
    const projection = dx * this.direction.x + dy * this.direction.y;

    // Check if projection is within beam length (accounting for entity radius)
    if (projection < -entityRadius || projection > beamLen + entityRadius) return false;

    // Calculate perpendicular distance to beam line
    const perpX = this.origin.x + this.direction.x * Math.max(0, Math.min(projection, beamLen));
    const perpY = this.origin.y + this.direction.y * Math.max(0, Math.min(projection, beamLen));
    const pdx = entityX - perpX;
    const pdy = entityY - perpY;
    const perpDistance = Math.sqrt(pdx * pdx + pdy * pdy);

    // Entity is hit if perpendicular distance minus entity radius is within beam width
    return perpDistance <= (this.width / 2) + entityRadius;
  }

  private renderOverchargeOrb(ctx: CanvasRenderingContext2D, pos: Vector2, zoom: number): void {
    const t = this.time;
    const charge = Math.min(1, this.channelTime / 3.0); // 0-1 over 3 seconds
    const baseRadius = (8 + charge * 20) * zoom;
    const pulse = Math.sin(t * 12) * 0.1 + 1;
    const radius = baseRadius * pulse;

    // Outer glow (grows with charge)
    const glowRadius = radius * 2.5;
    ctx.globalAlpha = 0.2 + charge * 0.3;
    const glowGrad = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, glowRadius);
    glowGrad.addColorStop(0, this.color + 'cc');
    glowGrad.addColorStop(0.5, this.color + '44');
    glowGrad.addColorStop(1, this.color + '00');
    ctx.fillStyle = glowGrad;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    // Core orb
    ctx.globalAlpha = 0.7 + charge * 0.3;
    const coreGrad = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, radius);
    coreGrad.addColorStop(0, '#ffffff');
    coreGrad.addColorStop(0.3, this.secondaryColor);
    coreGrad.addColorStop(0.7, this.color);
    coreGrad.addColorStop(1, this.color + '00');
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
    ctx.fill();

    // Orbiting sparks
    const sparkCount = Math.floor(3 + charge * 5);
    ctx.globalAlpha = 0.8;
    for (let i = 0; i < sparkCount; i++) {
      const angle = (t * 4 + i * Math.PI * 2 / sparkCount);
      const dist = radius * (0.8 + Math.sin(t * 8 + i) * 0.3);
      const sx = pos.x + Math.cos(angle) * dist;
      const sy = pos.y + Math.sin(angle) * dist;
      const sparkSize = (2 + charge * 2) * zoom;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(sx, sy, sparkSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /**
   * Get the beam's element type
   */
  getElement(): ElementType {
    return this.element;
  }

  /**
   * Get the beam's DPS value from skill
   */
  getDPS(): number {
    return SkillCreatorV2.getBeamDPS(this.skill);
  }

  /**
   * Get the beam range (for viewport culling bounding box)
   */
  getRange(): number {
    return this.range;
  }

  /**
   * Get beam mode choice
   */
  getBeamMode(): 'steady' | 'rampup' | 'overcharge' {
    return this.beamMode;
  }

  /**
   * Get total channel time (seconds since beam started)
   */
  getChannelTime(): number {
    return this.channelTime;
  }
}

/**
 * Factory function to create beam effect from skill
 */
export function createBeamEffect(config: BeamEffectConfig): BeamEffect {
  return new BeamEffect(config);
}
